function createAllowedPaths<T extends readonly string[]>(
  paths: "/" extends T[number] ? never : T,
): T {
  return paths;
}

export const config = {
  i18n: {
    locales: {
      en: {
        currency: "USD",
        label: "English",
      },
      es: {
        currency: "USD",
        label: "Español",
      },
    },
    defaultLocale: "en",
    defaultCurrency: "USD",
    cookieName: "NEXT_LOCALE",
  },
  auth: {
    cookieName: "auth_session",
    redirectAfterLogout: "/",
  },
  emails: {
    defaultFrom: {
      name: "Venturu",
      email: "info@hey.venturu.com",
    },
    defaultReplyTo: "support@venturu.com",
    brokerSendDomain: "hey.venturu.com",
  },
  allowedRedirectPaths: createAllowedPaths([
    // DO NOT ADD / (root) HERE
    "/api/crm/conversations",
    "/auth/forgot-password/reset",
    "/auth/mcp/authorize",
    "/auth/verify",
    "/businesses",
    "/business",
    "/chat",
    "/org-invite",
    "/invitations",
    "/settings",
    "/my-searches",
    "/os",
    "/professional-access",
  ] as const),
  newListingFlow: {
    totalSteps: 5,
    minimumImagesRequired: 0,
    maxImageWidth: 8192,
    maxImageHeight: 8192,
    maximumImagesAllowed: 40,
    descriptionMaxLength: 500,
    pitchMaxLength: 85,
    minimumPassingTrustScore: 60,
    sellerDocumentIngestionEnabled:
      process.env.SELLER_DOCUMENT_INGESTION_ENABLED !== "false",
    sellerDocumentMaxBytes: 25 * 1024 * 1024,
  },
  profile: {
    maxBioLength: 500,
    maxAgentSkills: 10,
    maxAgentIndustrySpecialties: 10,
  },
  listings: {
    fetchLimit: 21,

    mapMarkers: {
      far: {
        zoomRange: [0, 5],
        maxFull: 10,
        maxCollapsed: 5,
        targetSubidivisionLevel: 1,
        nPerSubdivision: 3,
      },

      near: {
        zoomRange: [6, 12],
        maxFull: 15,
        maxCollapsed: 5,
        targetSubidivisionLevel: 3,
        nPerSubdivision: 4,
      },

      close: {
        zoomRange: [13, Number.POSITIVE_INFINITY],
        maxFull: Number.POSITIVE_INFINITY,
        maxCollapsed: Number.POSITIVE_INFINITY,
        targetSubidivisionLevel: undefined,
        nPerSubdivision: undefined,
      },
    },
  },
  agents: {
    fetchLimit: 30,
    matching: {
      reminderDelay: "6h",
      rotationDelay: "12h",
    },
  },
  crm: {
    reminders: {
      delays: {
        first: "24h",
        second: "2d",
        third: "3d",
      },
    },
    autoreplyDelay: "5s",
    inquiryRateLimits: {
      burst: {
        tokens: 3,
        window: "60 s",
      },
      daily: {
        tokens: 20,
        window: "24 h",
      },
    },
  },
  mediaQuery: {
    sm: "only screen and (min-width: 40rem)",
    md: "only screen and (min-width: 48rem)",
    lg: "only screen and (min-width: 64rem)",
    xl: "only screen and (min-width: 80rem)",
    "2xl": "only screen and (min-width: 1440px)",
  },
  reviews: {
    maxDescriptionLength: 1000,
  },
  importer: {
    maxDocuments: 50,
  },
  listingScoreRules: {
    title: {
      exists: 3,
    },
    description: {
      minLength: 50,
      satisfiesMinimum: 4,
    },
    businessType: {
      exists: 3,
    },
    establishedAt: {
      exists: 3,
    },
    ownerWeeklyHours: {
      exists: 3,
    },
    employeeCount: {
      exists: 3,
    },
    property: {
      exists: 5,
    },
    training: {
      exists: 3,
    },
    revenue: {
      exists: 2,
    },
    profit: {
      exists: 2,
    },
    sde: {
      exists: 2,
    },
    media: {
      minImages: 1,
      maxImages: 8,
      satisfiesMinimum: 6,
      pointsPerAdditionalImages: 2,
    },
    sellerAgent: {
      exists: 15,
    },
    valuation: {
      started: 10,
      completed: 5,
    },
    askingPrice: {
      lessThanMaxValuation: 8,
      lessThanMinValuation: 12,
    },
  },
  ai: {
    models: {
      listingPitch: "google/gemini-2.5-flash",
      listingBusinessType: "google/gemini-2.5-flash",
      listingDescription: "google/gemini-2.5-flash",
      listingTrainingDetails: "google/gemini-2.5-flash",
      listingPropertyDetails: "google/gemini-2.5-flash",
      listingAdConfig: "google/gemini-2.5-flash",
      listingAdImagePrompt: "google/gemini-2.5-flash",
      listingAdImageGeneration: "bytedance/seedream-4",
      brokerLeadPortraitGeneration: "openai/gpt-image-2.5-sunburst",
      brokerLeadPortraitOutpaint: "black-forest-labs/flux-2-klein-9b",
      brokerLeadPortraitGenerationFallback: "openai/gpt-image-2",
      brokerLeadCreativeQa: "google/gemini-3-flash-preview",
      listingAIPlaceholderImagePrompt: "google/gemini-2.5-flash",
      listingAIPlaceholderImageGeneration: "bytedance/seedream-4",
      listingTrustScoreAssessment: "google/gemini-2.5-flash",
      businessTypeIcon: "google/nano-banana",
      businessTypeImageDiffusionPrompt: "google/gemini-2.5-flash",
      businessTypeImageDiffusionGeneration: "bytedance/seedream-4",
      glossaryTerm: "google/gemini-2.5-flash",
      venturuOneMarketAnalysis: "google/gemini-3-flash-preview",
      listingExportCopy: "google/gemini-2.5-flash",
      blogTakeaways: "google/gemini-2.5-flash",
      qualifiedLeadExtract: "google/gemini-3-flash-preview",
      leadBatchExtract: "google/gemini-2.5-flash",
      leadCsvMapping: "google/gemini-2.5-flash",
      socialShareCaption: "google/gemini-2.5-flash",
      outreachNoteSummary: "google/gemini-3.1-flash-lite-preview",
      dealResearchBrief: "google/gemini-3.1-flash-lite-preview",
      intakeFormTranslate: "google/gemini-2.5-flash",
    },
  },
  integrations: {
    metaPixel: "762258956201613",
    googleTagManager: "G-2SRZR36D3G",
    promptWatch: "620712c7-2afb-4207-ad93-128a8033548d",
    openaiApps: "bM5rK4EYtP8gdUH0Gxq_z1fpRuSqJFeFd7kZWYZcJT0",
  },
  socialShare: {
    enabled: true,
    provider: "buffer",
    channels: ["facebook", "instagram", "tiktok", "linkedin"],
    listings: {
      enabled: true,
      postsPerWeek: 7,
      postTimeHour: 10,
      minRecommendedScore: 5,
      schedulingMode: "recommendedTime",
    },
    brokers: {
      enabled: false,
      postsPerWeek: 1,
      postDay: 1,
      postTimeHour: 12,
      minRecommendedScore: 5,
      schedulingMode: "recommendedTime",
    },
    blogPosts: {
      enabled: true,
    },
    scheduling: {
      timezone: "America/New_York",
      cronDay: 6,
      cronHour: 8,
    },
  },
  ads: {
    platformFee: 0.3,
    minCreditsPurchase: 10000,
    maxCreditsPurchase: 100000,
    recommendedCreditsPurchase: 25000,
    disallowedBusinessTypeSlugs: [
      // Plainly banned
      "firearm-retailer-shooting-range",
      "smoke-shop",
      "pharmacy",
      "casino",

      // High-risk financial
      "check-cashing-financial-services",
      "pawn-shop",
      "banking-and-loan-business",
    ],
  },
  one: {
    monthlyPriceCents: 200000,
    yearlyPriceCents: 1800000,
    setupFeeCents: 250000,
    monthlyZipCodeCount: 1,
    annualZipCodeCount: 3,
  },
  leadGenerationPlans: {
    /** Territories included with Venturu OS (no separate Lead Generation tier). */
    includedTerritories: 3,
    maxProfilePhotos: 5,
    /** Max broker-initiated regenerations per ad in a rolling 30-day window. */
    maxUserCreativeRegenerations: 3,
    /** Max square-photo replacements per creative (outpainted to other ratios). */
    maxUserCreativeImageOverrides: 3,
    /** Live AI drafts of source facts for ads. Cache hits do not count. */
    focusCopy: {
      burst: { tokens: 8, window: "10 m" },
      monthly: { tokens: 40, window: "30 d" },
    },
    creativePipeline: {
      candidateCount: 4,
      useTechnicalGates: true,
      shadowMode: true,
    },
    expansionPostcodeFeeCents: 50_000,
    territory: {
      recommendedMinCounties: 3,
      maxCounties: 8,
    },
    reachGate: {
      targetCpmCents: 7_000,
      frequencyHeadroom: 3,
      minReach: 250_000,
    },
    annualDiscountBps: 2000,
    budgetRange: {
      minCents: 50_000,
      maxCents: 1_000_000,
      /** Monthly minimums in USD cents. Omitted countries use minCents. */
      minCentsByCountry: {
        nz: 25_000,
      },
    },
    tiers: {
      PRODUCER: {
        platformFeeCentsMonthly: 120_000,
        includedTerritories: 1,
        features: ["ai_creative_suite"],
        marketingBullets: ["1 Exclusive Territory", "AI Creative Suite"],
      },
      RAINMAKER: {
        platformFeeCentsMonthly: 200_000,
        includedTerritories: 3,
        features: ["ai_creative_suite", "advanced_retargeting"],
        marketingBullets: ["3 Exclusive Territories", "Advanced Retargeting"],
      },
      TYCOON: {
        platformFeeCentsMonthly: 400_000,
        includedTerritories: 999,
        features: [
          "ai_creative_suite",
          "advanced_retargeting",
          "statewide_coverage",
          "white_label",
        ],
        marketingBullets: ["Statewide Coverage", "White-Label Options"],
      },
    },
  },
  osCampaigns: {
    /** Max emails a workspace may send (or schedule) per calendar day (UTC). */
    dailyEmailSendLimit: 500,
    /** Minimum time between marketing sends to the same contact. */
    defaultContactCooldownDays: 7,
    defaultMailingListTags: ["Sellers", "Buyers", "Investors", "Owners"],
  },
  supportedCountries: ["us", "ca", "nz"],
  mcp: {
    /** Emails that skip real CRM sends from MCP contact tools (test users). */
    blockedTestEmails: [
      "openai_demo@venturu.com",
      "anthropic_demo@venturu.com",
    ],
  },
} as const satisfies Config;

export type Config = {
  i18n: {
    locales: { [locale: string]: { currency: string; label: string } };
    defaultLocale: string;
    defaultCurrency: string;
    cookieName: string;
  };
  auth: {
    cookieName: string;
    redirectAfterLogout: string;
  };
  emails: {
    defaultFrom: {
      name: string;
      email: string;
    };
    defaultReplyTo: string;
    brokerSendDomain: string;
  };
  allowedRedirectPaths: string[];
  newListingFlow: {
    totalSteps: number;
    minimumImagesRequired: number;
    maxImageWidth: number;
    maxImageHeight: number;
    maximumImagesAllowed: number;
    descriptionMaxLength: number;
    pitchMaxLength: number;
    minimumPassingTrustScore: number;
    sellerDocumentIngestionEnabled: boolean;
    sellerDocumentMaxBytes: number;
  };
  profile: {
    maxBioLength: number;
    maxAgentSkills: number;
    maxAgentIndustrySpecialties: number;
  };
  listings: {
    fetchLimit: number;
    mapMarkers: {
      far: {
        zoomRange: [number, number];
        maxFull: number;
        maxCollapsed: number;
        targetSubidivisionLevel?: number;
        nPerSubdivision?: number;
      };

      near: {
        zoomRange: [number, number];
        maxFull: number;
        maxCollapsed: number;
        targetSubidivisionLevel?: number;
        nPerSubdivision?: number;
      };

      close: {
        zoomRange: [number, number];
        maxFull: number;
        maxCollapsed: number;
        targetSubidivisionLevel?: number;
        nPerSubdivision?: number;
      };
    };
  };
  agents: {
    fetchLimit: number;
    matching: {
      reminderDelay: string;
      rotationDelay: string;
    };
  };
  crm: {
    reminders: {
      delays: {
        first: string;
        second: string;
        third: string;
      };
    };
    autoreplyDelay: string;
    inquiryRateLimits: {
      burst: {
        tokens: number;
        window: string;
      };
      daily: {
        tokens: number;
        window: string;
      };
    };
  };
  mediaQuery: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
    "2xl": string;
  };
  reviews: {
    maxDescriptionLength: number;
  };
  importer: {
    maxDocuments: number;
  };
  listingScoreRules: {
    title: {
      exists: number;
    };
    description: {
      minLength: number;
      satisfiesMinimum: number;
    };
    businessType: {
      exists: number;
    };
    establishedAt: {
      exists: number;
    };
    ownerWeeklyHours: {
      exists: number;
    };
    employeeCount: {
      exists: number;
    };
    property: {
      exists: number;
    };
    training: {
      exists: number;
    };
    revenue: {
      exists: number;
    };
    profit: {
      exists: number;
    };
    sde: {
      exists: number;
    };
    media: {
      minImages: number;
      maxImages: number;
      satisfiesMinimum: number;
      pointsPerAdditionalImages: number;
    };
    sellerAgent: {
      exists: number;
    };
    valuation: {
      started: number;
      completed: number;
    };
    askingPrice: {
      lessThanMaxValuation: number;
      lessThanMinValuation: number;
    };
  };
  ai: {
    models: {
      listingPitch: string;
      listingBusinessType: string;
      listingDescription: string;
      listingTrainingDetails: string;
      listingPropertyDetails: string;
      listingAdConfig: string;
      listingAdImagePrompt: string;
      listingAdImageGeneration: string;
      brokerLeadPortraitGeneration: string;
      brokerLeadPortraitOutpaint: string;
      brokerLeadPortraitGenerationFallback: string;
      brokerLeadCreativeQa: string;
      listingAIPlaceholderImagePrompt: string;
      listingAIPlaceholderImageGeneration: string;
      listingTrustScoreAssessment: string;
      businessTypeIcon: string;
      businessTypeImageDiffusionPrompt: string;
      businessTypeImageDiffusionGeneration: string;
      glossaryTerm: string;
      venturuOneMarketAnalysis: string;
      listingExportCopy: string;
      blogTakeaways: string;
      qualifiedLeadExtract: string;
      leadBatchExtract: string;
      leadCsvMapping: string;
      socialShareCaption: string;
      outreachNoteSummary: string;
      dealResearchBrief: string;
      intakeFormTranslate: string;
    };
  };
  integrations: {
    metaPixel: string;
    googleTagManager: string;
    promptWatch: string;
    openaiApps: string;
  };
  socialShare: {
    enabled: boolean;
    provider: "buffer";
    channels: Array<"facebook" | "instagram" | "tiktok" | "linkedin">;
    listings: {
      enabled: boolean;
      postsPerWeek: number;
      postTimeHour: number;
      minRecommendedScore: number;
      schedulingMode: "customScheduled" | "recommendedTime";
    };
    brokers: {
      enabled: boolean;
      postsPerWeek: number;
      postDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
      postTimeHour: number;
      minRecommendedScore: number;
      schedulingMode: "customScheduled" | "recommendedTime";
    };
    blogPosts: {
      enabled: boolean;
    };
    scheduling: {
      timezone: string;
      cronDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
      cronHour: number;
    };
  };
  ads: {
    platformFee: number;
    minCreditsPurchase: number;
    maxCreditsPurchase: number;
    recommendedCreditsPurchase: number;
    disallowedBusinessTypeSlugs: string[];
  };
  one: {
    monthlyPriceCents: number;
    yearlyPriceCents: number;
    setupFeeCents: number;
    monthlyZipCodeCount: number;
    annualZipCodeCount: number;
  };
  leadGenerationPlans: {
    includedTerritories: number;
    maxProfilePhotos: number;
    maxUserCreativeRegenerations: number;
    maxUserCreativeImageOverrides: number;
    focusCopy: {
      burst: { tokens: number; window: string };
      monthly: { tokens: number; window: string };
    };
    creativePipeline: {
      candidateCount: number;
      useTechnicalGates: boolean;
      shadowMode: boolean;
    };
    expansionPostcodeFeeCents: number;
    territory: {
      recommendedMinCounties: number;
      maxCounties: number;
    };
    reachGate: {
      targetCpmCents: number;
      frequencyHeadroom: number;
      minReach: number;
    };
    annualDiscountBps: number;
    budgetRange: {
      minCents: number;
      maxCents: number;
      minCentsByCountry: Readonly<Record<string, number>>;
    };
    tiers: Record<
      "PRODUCER" | "RAINMAKER" | "TYCOON",
      {
        platformFeeCentsMonthly: number;
        includedTerritories: number;
        features: string[];
        marketingBullets: string[];
      }
    >;
  };
  osCampaigns: {
    dailyEmailSendLimit: number;
    defaultContactCooldownDays: number;
    defaultMailingListTags: string[];
  };
  supportedCountries: string[];
  mcp: {
    blockedTestEmails: string[];
  };
};

export type Locale = keyof (typeof config)["i18n"]["locales"];

export type SupportedCountry = (typeof config)["supportedCountries"][number];
